import { motion } from 'framer-motion';

import useDetailModalStore, {
  DETAIL_MODAL_TYPE,
} from '@/stores/pure/DetailModalStore';
import {
  getFeedbackSourceName,
  getSourceIcon,
} from '@/types/feedbackConst';

import {
  formatFeedbackGalleryEngagementValue,
  resolveFeedbackGalleryEngagementItems,
  resolveFeedbackGalleryOriginHref,
  resolveFeedbackGalleryProfileMeta,
  resolveFeedbackGalleryTitle,
  resolveFeedbackGalleryVisibleBlocks,
} from '../model/feedback-gallery-card-view-model';
import FeedbackGalleryCardBlockList from './feedback-gallery-card-block-list';
import FeedbackGalleryCardEngagement from './feedback-gallery-card-engagement';
import FeedbackGalleryCardHeader from './feedback-gallery-card-header';

type FeedbackGalleryCardProps = {
  feedback: FeedbackListItemT;
  animationIndex?: number;
  disableClick?: boolean;
};

const FeedbackGalleryCard = ({
  feedback,
  animationIndex = 0,
  disableClick = false,
}: FeedbackGalleryCardProps) => {
  const setNewModal = useDetailModalStore((state) => state.setNewModal);
  const initModal = useDetailModalStore((state) => state.initModal);
  const sourceIcon = getSourceIcon(feedback.source?.type, 24);

  const sourceName = feedback.source?.type
    ? getFeedbackSourceName(feedback.source.type)
    : 'Unknown source';
  const title = resolveFeedbackGalleryTitle(feedback);
  const profileMeta = resolveFeedbackGalleryProfileMeta(feedback);
  const originHref = resolveFeedbackGalleryOriginHref(feedback);
  const visibleBlocks = resolveFeedbackGalleryVisibleBlocks(feedback);
  const engagementItems = resolveFeedbackGalleryEngagementItems(feedback);

  const handleCardClick = () => {
    initModal();
    setNewModal({
      alias: feedback.alias,
      type: DETAIL_MODAL_TYPE.FEEDBACK,
    });
  };
  const getTooltipContent = (label: string, value: string) => (
    <div className="max-w-[360px] space-y-1 text-left">
      <p className="text-[10px] font-semibold uppercase tracking-[0.08em] text-background/70">
        {label}
      </p>
      <p className="text-xs leading-relaxed whitespace-pre-wrap break-words text-background">
        {value}
      </p>
    </div>
  );
  const animationDelay = Math.min((animationIndex % 12) * 0.03, 0.33);

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        duration: 0.28,
        ease: [0.22, 1, 0.36, 1],
        delay: animationDelay,
      }}
      className={`mb-5 break-inside-avoid divide-y divide-gray-200 rounded-xl border border-gray-300/50 bg-white shadow-[0_1px_4px_rgba(0,0,0,0.06)] transition-all will-change-transform ${disableClick ? '' : 'cursor-pointer hover:border-gray-400 hover:shadow-[0_2px_8px_rgba(0,0,0,0.1)]'}`}
      onClick={disableClick ? undefined : handleCardClick}
    >
      <FeedbackGalleryCardHeader
        sourceName={sourceName}
        sourceIcon={sourceIcon}
        title={title}
        profileMeta={profileMeta}
        originHref={originHref}
        getTooltipContent={getTooltipContent}
      />
      <FeedbackGalleryCardBlockList
        blocks={visibleBlocks}
        getTooltipContent={getTooltipContent}
      />
      <FeedbackGalleryCardEngagement
        engagementItems={engagementItems}
        getTooltipContent={getTooltipContent}
        formatEngagementValue={formatFeedbackGalleryEngagementValue}
      />
    </motion.div>
  );
};

export default FeedbackGalleryCard;
